import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { IMessageThreadTurn } from "../../../../../../../@types/MessageThread";
import { Book } from "../../../../../../../assets/icons/Book";
import { CheckedOption } from "../../../../../../../assets/icons/CheckedOption";
import { UncheckedOption } from "../../../../../../../assets/icons/UncheckedOption";
import { getChatTimestamp } from "../../../../../../../utils/dateFormatter";
import { mixpanelActions } from "../../../../../../../utils/mixpanel";
import {
  AnsweredOption,
  ArticleCard,
  ArticleCardIcon,
  ArticleCardLabel,
  ArticleCardText,
  ArticleCardTitle,
  MessageContainer,
  MessageLine,
  MessageTitle,
  Option,
  OptionsContainer,
  Timestamp,
} from "../MessageThreadModal.styles";

type ThreadBubbleProps = {
  turn: IMessageThreadTurn;
  /** Rendered above the first bubble only, the way a subject line reads. */
  subject?: string;
  /** The turn is the patient's pick on a multiple-choice question; its body is the option text. */
  isAnsweredOption?: boolean;
};

function ResponseOption({
  text,
  selected,
}: {
  text: string;
  selected: boolean;
}) {
  return (
    <Option isSelected={selected}>
      <p>{text}</p>
      {selected ? <CheckedOption /> : <UncheckedOption />}
    </Option>
  );
}

function ArticleAttachment({ turn }: { turn: IMessageThreadTurn }) {
  const { t } = useTranslation();

  if (!turn.articleId) return null;

  if (!turn.article) {
    return (
      <ArticleCard as="div" disabled>
        <ArticleCardIcon>
          <Book width={18} height={18} />
        </ArticleCardIcon>
        <ArticleCardText>
          <ArticleCardLabel>
            {t("patientPage_messages_view_attachedArticle")}
          </ArticleCardLabel>
          <ArticleCardTitle disabled>
            {t("patientPage_messages_articleUnavailable")}
          </ArticleCardTitle>
        </ArticleCardText>
      </ArticleCard>
    );
  }

  const { article } = turn;

  const handleArticleClick = () => {
    mixpanelActions.track("HCP Opened Article From Message Thread", {
      articleId: article.id,
      messageId: turn.messageId,
    });
  };

  return (
    <ArticleCard
      href={`/careinfo/view/${article.id}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleArticleClick}
    >
      <ArticleCardIcon>
        <Book width={18} height={18} />
      </ArticleCardIcon>
      <ArticleCardText>
        <ArticleCardLabel>
          {t("patientPage_messages_view_attachedArticle")}
        </ArticleCardLabel>
        <ArticleCardTitle title={article.title}>
          {article.title}
        </ArticleCardTitle>
      </ArticleCardText>
    </ArticleCard>
  );
}

export function ThreadBubble({
  turn,
  subject,
  isAnsweredOption,
}: ThreadBubbleProps) {
  const { t } = useTranslation();

  const isPatient = turn.author === "Patient";
  const timestamp = getChatTimestamp(turn.timestamp);

  if (isAnsweredOption) {
    return (
      <MessageContainer isPatientMessage>
        <AnsweredOption>
          <ResponseOption text={turn.body} selected />
        </AnsweredOption>
        <Timestamp>{timestamp}</Timestamp>
      </MessageContainer>
    );
  }

  const lines = turn.body?.split("\n") ?? [];

  return (
    <MessageContainer isPatientMessage={isPatient}>
      {subject && <MessageTitle>{subject}</MessageTitle>}

      <div>
        {lines.map((line) => (
          <MessageLine key={uuidv4()}>{line.trim()}</MessageLine>
        ))}

        {turn.responseType === "Options" && turn.responseOptions?.length ? (
          <OptionsContainer>
            {[...turn.responseOptions]
              .sort((a, b) => a.index - b.index)
              .map((option) => (
                <ResponseOption
                  key={option.id}
                  text={option.text}
                  selected={option.selected}
                />
              ))}
          </OptionsContainer>
        ) : null}
      </div>

      <ArticleAttachment turn={turn} />

      {/* Read state is only meaningful on what we sent; a patient's message reaching us is not news. */}
      <Timestamp>
        {isPatient
          ? timestamp
          : t(
              turn.isRead
                ? "patientPage_messages_thread_staffTimestampRead"
                : "patientPage_messages_thread_staffTimestampUnread",
              { staffName: turn.staffName, timestamp }
            )}
      </Timestamp>
    </MessageContainer>
  );
}
